import {forkJoin, map, Observable, of, throwError} from 'rxjs';
import {
    BaseCollectionModel,
    SearchModel,
    YascEntityApiAdapterV3,
    YascEntityApiRoutesV3,
    yascCreateEntityApiRoutesV3,
} from '@yasc/yasc-material-components';
import {RestApiService} from '../../../rest-api.service';
import {
    BaseTaskRes,
    ProjectCreateReq,
    ProjectMinRes,
    ProjectRoleRes,
    ProjectSummaryAggregationRes,
    ProjectSummaryGroupingRes,
    ProjectTrackerRes,
    ProjectUpdateReq,
} from '../../../../contracts/v0/crm';
import {ApiV0Crm} from '../api.v0.crm';
import {InvoiceDetailRes} from '../../../../contracts/v0/cp/invoice.contract';
import {EntityMergeResult} from '../../../../contracts/entity-merge.contract';

type ProjectDetailResponse = ProjectTrackerRes & ProjectMinRes;

export class ApiV0CrmProject implements YascEntityApiAdapterV3<ProjectMinRes, ProjectTrackerRes, ProjectCreateReq, ProjectUpdateReq, number> {
    readonly modelTypes = {
        row: ProjectMinRes,
        detail: ProjectTrackerRes,
        create: ProjectCreateReq,
        patch: ProjectUpdateReq,
    };

    readonly idKey = 'id' as const;
    readonly segment: string;
    readonly routes: YascEntityApiRoutesV3<number>;

    constructor(v0: ApiV0Crm, private readonly rest: RestApiService) {
        this.segment = `${v0.segment}/project`;
        this.routes = yascCreateEntityApiRoutesV3<number>(this.segment);
    }

    search(searchModel: SearchModel = new SearchModel({})): Observable<BaseCollectionModel<ProjectMinRes>> {
        return this.searchWithSummary(searchModel).pipe(
            map(result => result as unknown as BaseCollectionModel<ProjectMinRes>),
        );
    }

    searchWithSummary(searchModel: SearchModel = new SearchModel({})): Observable<BaseCollectionModel<ProjectMinRes, ProjectSummaryAggregationRes, ProjectSummaryGroupingRes>> {
        return this.rest.post<SearchModel, BaseCollectionModel<ProjectMinRes, ProjectSummaryAggregationRes, ProjectSummaryGroupingRes>>(
            `${this.segment}/search`,
            ProjectMinRes,
            searchModel,
        );
    }

    detail(id: number): Observable<ProjectTrackerRes> {
        return this.rest.get<ProjectTrackerRes>(`${this.segment}/${id}`, ProjectTrackerRes);
    }

    details(id: number): Observable<ProjectTrackerRes> {
        return this.detail(id);
    }

    create(createReq: ProjectCreateReq): Observable<ProjectDetailResponse> {
        return this.rest.post<ProjectCreateReq, ProjectDetailResponse>(
            this.segment,
            ProjectTrackerRes as any,
            createReq,
        );
    }

    patch(id: number, updateReq: ProjectUpdateReq): Observable<ProjectDetailResponse> {
        return this.rest.patch<ProjectUpdateReq, ProjectDetailResponse>(
            `${this.segment}/${id}`,
            ProjectTrackerRes as any,
            updateReq,
        );
    }

    update(updateReq: ProjectUpdateReq, id?: number): Observable<ProjectDetailResponse> {
        const projectId = id ?? updateReq.id;
        if (!projectId) {
            return throwError(() => new Error('Project update requires an id.'));
        }

        return this.patch(projectId, updateReq);
    }

    patchProject(id: number, updateReq: ProjectUpdateReq): Observable<ProjectDetailResponse> {
        return this.patch(id, updateReq);
    }

    delete(ids: number[]): Observable<void> {
        const distinctIds = [...new Set((ids ?? []).filter(id => id > 0))];
        if (!distinctIds.length) {
            return of(undefined);
        }

        return this.bulkDelete(distinctIds);
    }

    bulkDelete(ids: number[]): Observable<void> {
        const distinctIds = [...new Set((ids ?? []).filter(id => id > 0))];
        if (!distinctIds.length) {
            return of(undefined);
        }

        return this.rest.post<number[], unknown>(`${this.segment}/batch`, Object as any, distinctIds).pipe(map(() => undefined));
    }

    mergeOptions(): Observable<BaseCollectionModel<ProjectMinRes>> {
        return this.rest.get<BaseCollectionModel<ProjectMinRes>>(
            `${this.segment}/merge-options`,
            ProjectMinRes,
        );
    }

    merge(sourceId: number, targetId: number): Observable<EntityMergeResult<number>> {
        return this.rest.post<{targetId: number}, EntityMergeResult<number>>(
            `${this.segment}/${sourceId}/merge`,
            Object as any,
            {targetId},
        );
    }

    createMany(projectCreateRequestList: ProjectCreateReq[]): Observable<BaseCollectionModel<ProjectTrackerRes>> {
        if (!projectCreateRequestList.length) {
            return of(new BaseCollectionModel<ProjectTrackerRes>([], 0));
        }

        return forkJoin(projectCreateRequestList.map(request => this.create(request))).pipe(
            map(items => new BaseCollectionModel<ProjectTrackerRes>(items, items.length)),
        );
    }

    updateMany(projectUpdateRequestList: ProjectUpdateReq[]): Observable<BaseCollectionModel<ProjectTrackerRes>> {
        if (!projectUpdateRequestList.length) {
            return of(new BaseCollectionModel<ProjectTrackerRes>([], 0));
        }

        return forkJoin(projectUpdateRequestList.map(request => this.update(request))).pipe(
            map(items => new BaseCollectionModel<ProjectTrackerRes>(items, items.length)),
        );
    }

    getDistinctNames(): Observable<BaseCollectionModel<string>> {
        return this.rest.get<BaseCollectionModel<string>>(`${this.segment}/names`);
    }

    tasks(id: number): Observable<BaseCollectionModel<BaseTaskRes>> {
        return this.rest.get<BaseCollectionModel<BaseTaskRes>>(`${this.segment}/${id}/tasks`, BaseTaskRes);
    }

    team(id: number): Observable<BaseCollectionModel<ProjectRoleRes>> {
        return this.rest.get<BaseCollectionModel<ProjectRoleRes>>(`${this.segment}/${id}/team`, ProjectRoleRes);
    }

    invoices(id: number): Observable<BaseCollectionModel<InvoiceDetailRes>> {
        return this.rest.get<BaseCollectionModel<InvoiceDetailRes>>(`${this.segment}/${id}/invoices`, InvoiceDetailRes);
    }
}
